import * as React from 'react';

import { useInViewWithDwell } from '@notino/react-web-utils';
import type { AdProvider, TSponsoredAdsEvents } from '@notino/web-tracking';

import { MIN_SPONSORED_PRODUCTS } from '../SponsoredAdsProducts/constants';

import {
  asBelowMinimumUnrendered,
  getPlacementPayloads,
  getRenderedOccurrences,
  getSponsoredPlacementInViewOptions,
  partitionOccurrencesByRender,
  shouldReportClickOnContextMenu,
  shouldReportInteractionsOnce,
  sponsoredAdsReporter,
  type RenderedOccurrence,
  type SponsoredAdsOccurrence,
} from './tracking';

type Carousel = Parameters<typeof getPlacementPayloads>[0];

/**
 * Interaction reports that are single-use where the provider requires it (Criteo):
 * the same product must not report a second click / basket change / wishlist add for
 * one delivery. `ProductOnView` is intentionally absent — it is a viewability signal
 * rather than an interaction.
 */
const SINGLE_USE_EVENT_TYPES = new Set<TSponsoredAdsEvents['type']>([
  'ProductOnClick',
  'ProductOnBasketChange',
  'ProductOnWishlist',
]);

interface UseSponsoredAdsTrackingParams {
  carousel: Carousel;
  provider: AdProvider;
  occurrences: SponsoredAdsOccurrence[];
  cappedOccurrences: SponsoredAdsOccurrence[];
}

/**
 * Owns every sponsored-ads tracking side effect for the product-detail placement:
 * the placement `onLoad`/`onView` beacons and the per-product click/impression/
 * basket/wishlist handlers wired into ProductsSection.
 */
export const useSponsoredAdsTracking = ({
  carousel,
  provider,
  occurrences,
  cappedOccurrences,
}: UseSponsoredAdsTrackingParams) => {
  // Only rendered occurrences can be interacted with, and they are the ones that carry a
  // slot. First-occurrence wins: a duplicated product can only be disambiguated by slot
  // at click/view time, which ProductsSection does not expose (productId only).
  const trackingByCatalogId = React.useMemo(() => {
    const map = new Map<string, RenderedOccurrence>();
    for (const occurrence of getRenderedOccurrences(cappedOccurrences)) {
      if (!map.has(occurrence.productId)) {
        map.set(occurrence.productId, occurrence);
      }
    }
    return map;
  }, [cappedOccurrences]);

  // ProductsSection reports the web `id`, but occurrences are keyed by Criteo's
  // catalog id. Bridge web id -> catalog id from the resolved `vpProduct` (which
  // exposes both), now returned inline by the ProductDetailAds query.
  const catalogIdByWebId = React.useMemo(() => {
    const map = new Map<string, string>();
    for (const { vpProduct } of cappedOccurrences) {
      if (vpProduct && vpProduct.id !== null && vpProduct.catalogId !== null) {
        map.set(String(vpProduct.id), String(vpProduct.catalogId));
      }
    }
    return map;
  }, [cappedOccurrences]);

  const getTracking = React.useCallback(
    (webId: string) =>
      trackingByCatalogId.get(catalogIdByWebId.get(webId) ?? webId),
    [trackingByCatalogId, catalogIdByWebId]
  );

  // Placement onLoad — fired once as soon as the delivery arrives, which is also when
  // the placement renders, reporting which slots actually rendered and which
  // ad-provided products could not be loaded (`vpProduct` came back null) as
  // `unrenderedProducts`. Resolution is part of the ProductDetailAds query, so the
  // occurrences already carry it — no extra product request is needed. A delivery too
  // small to fill the placement — few products delivered, or few of them loadable — is
  // reported too, as all-unrendered (see below).
  const loadReportedRef = React.useRef(false);
  React.useEffect(() => {
    if (loadReportedRef.current || !carousel) {
      return;
    }

    const onLoadPayload = getPlacementPayloads(carousel)?.onLoadPayload;
    if (!onLoadPayload) {
      return;
    }

    const { renderedProducts, unrenderedProducts } =
      partitionOccurrencesByRender(cappedOccurrences);

    const belowMinimum = renderedProducts.length < MIN_SPONSORED_PRODUCTS;

    loadReportedRef.current = true;

    sponsoredAdsReporter.report([
      {
        type: 'PlacementOnLoad',
        provider,
        payload: onLoadPayload,
        renderedProducts: belowMinimum ? [] : renderedProducts,
        unrenderedProducts: belowMinimum
          ? asBelowMinimumUnrendered(cappedOccurrences)
          : unrenderedProducts,
      },
    ]);
  }, [cappedOccurrences, carousel, provider]);

  // Placement onView — dwell-validated visibility (Criteo: ≥50% for 1000ms).
  const { ref: viewRef, inView: placementInView } = useInViewWithDwell({
    ...getSponsoredPlacementInViewOptions(provider),
    skip: !occurrences.length,
  });
  const viewReportedRef = React.useRef(false);
  React.useEffect(() => {
    if (!placementInView || viewReportedRef.current || !carousel) {
      return;
    }
    const onViewPayload = getPlacementPayloads(carousel)?.onViewPayload;
    if (!onViewPayload) {
      return;
    }
    viewReportedRef.current = true;
    sponsoredAdsReporter.report([
      { type: 'PlacementOnView', provider, payload: onViewPayload },
    ]);
  }, [placementInView, carousel, provider]);

  // Criteo only: repeat interactions that keep the user on the page — ctrl+click,
  // re-adding to the basket, re-wishlisting — must not re-report. Keyed by the opaque
  // payload, so a fresh delivery (new payloads) reports again while this placement
  // stays deduped.
  const singleUseInteractions = shouldReportInteractionsOnce(provider);
  const sentPayloadsRef = React.useRef(new Set<string>());

  // Product-level events: resolve the occurrence for the reported web id, let the
  // caller build the event from its payloads, and beacon it if there's anything to send.
  const report = React.useCallback(
    (
      webId: string,
      build: (tracking: RenderedOccurrence) => TSponsoredAdsEvents | undefined
    ) => {
      const tracking = getTracking(webId);
      const event = tracking && build(tracking);
      if (!event) {
        return;
      }

      if (singleUseInteractions && SINGLE_USE_EVENT_TYPES.has(event.type)) {
        const payloadKey = `${event.type}:${event.payload}`;
        if (sentPayloadsRef.current.has(payloadKey)) {
          return;
        }
        sentPayloadsRef.current.add(payloadKey);
      }

      sponsoredAdsReporter.report([event]);
    },
    [getTracking, singleUseInteractions]
  );

  const handlers = React.useMemo(
    () => ({
      onProductClick: (productId: string) =>
        report(productId, (t) =>
          t.onClickPayload
            ? {
                type: 'ProductOnClick',
                provider,
                payload: t.onClickPayload,
                slotId: t.slotId,
              }
            : undefined
        ),
      onProductImpression: (productId: string) =>
        report(productId, (t) =>
          t.onViewPayload
            ? { type: 'ProductOnView', provider, payload: t.onViewPayload }
            : undefined
        ),
      onProductBasketChange: (productId: string, quantity: number) =>
        report(productId, (t) =>
          t.onBasketChangePayload
            ? {
                type: 'ProductOnBasketChange',
                provider,
                payload: t.onBasketChangePayload,
                quantity,
              }
            : undefined
        ),
      onProductWishlist: (productId: string, wishlisted: boolean) =>
        report(productId, (t) =>
          // Contract only defines "added to wishlist"; ignore removals.
          wishlisted && t.onWishlistPayload
            ? {
                type: 'ProductOnWishlist',
                provider,
                payload: t.onWishlistPayload,
              }
            : undefined
        ),
    }),
    [report, provider]
  );

  return {
    viewRef,
    handlers,
    trackProductClickOnContextMenu: shouldReportClickOnContextMenu(provider),
  };
};
